import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, ShieldQuestion, Search } from "lucide-react";

export default function RADuplicateCheck() {
  const [f, setF] = useState({ company: "", position: "", location: "", email: "", phone: "", domain: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!f.company && !f.email && !f.domain) { toast.error("Enter a company, email or domain"); return; }
    setLoading(true);
    try { const { data } = await api.post("/ra/leads/duplicate-search", f); setResult(data); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  const allowed = result?.allowed;
  const Icon = result ? (allowed ? ShieldCheck : result.code === "possible_duplicate" || result.code === "duplicate" ? ShieldQuestion : ShieldAlert) : Search;
  const tone = result ? (allowed ? "text-emerald-600 bg-emerald-50" : "text-red-600 bg-red-50") : "text-[#255058] bg-[#255058]/10";

  return (
    <div>
      <PageHeader title="Check Duplicate Client" subtitle="Confirm whether research or outreach is permitted before you start" />
      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5 space-y-3">
          <Input placeholder="Company name" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} data-testid="dup-company" />
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Position" value={f.position} onChange={(e) => setF({ ...f, position: e.target.value })} data-testid="dup-position" />
            <Input placeholder="Location" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="dup-location" />
          </div>
          <Input placeholder="Contact email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="dup-email" />
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Contact phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
            <Input placeholder="Company domain" value={f.domain} onChange={(e) => setF({ ...f, domain: e.target.value })} />
          </div>
          <Button onClick={run} disabled={loading} data-testid="dup-search-btn" className="w-full gap-1.5 bg-[#255058] hover:bg-[#1c3d43] text-white">
            <Search className="h-4 w-4" /> {loading ? "Checking…" : "Check Duplicate"}
          </Button>
        </Card>

        <Card className="p-5 flex items-center justify-center min-h-[220px]" data-testid="dup-result">
          {!result ? (
            <div className="text-center text-muted-foreground text-sm">Run a check to see whether outreach is permitted.</div>
          ) : (
            <div className="text-center">
              <div className={`h-16 w-16 rounded-2xl grid place-items-center mx-auto ${tone}`}><Icon className="h-8 w-8" /></div>
              <div className="text-lg font-display font-semibold mt-4">{result.result}</div>
              {result.existing_status && <div className="text-sm text-muted-foreground mt-1">Existing status: {result.existing_status}{result.window_days ? ` · window ${result.window_days}d` : ""}</div>}
              <div className={`text-sm mt-2 font-medium ${allowed ? "text-emerald-600" : "text-red-600"}`}>{allowed ? "Research permitted" : "Outreach restricted — Admin authorization required"}</div>
            </div>
          )}
        </Card>
      </div>
      <p className="text-xs text-muted-foreground mt-3">This check never reveals BDM, sales, revenue, or another Research Analyst's identity.</p>
    </div>
  );
}
